import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { PageHeader } from '@/components/admin/AdminShell';
import { type Column, ListTable, type ListState } from '@/components/admin/ListTable';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { confirmScoreResultCsv, uploadScoreResultCsv, type CsvJob, type CsvRowResult, csvSampleUrl } from '@/features/csv/api';
import {
  fetchScoreVersions,
  publishScoreVersion,
  rollbackScoreVersion,
  scoreRawExportUrl,
  SCORE_VERSION_STATUS_LABELS,
  type ScoreVersion,
} from '@/features/scores/api';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * 採点管理（事務局のみ / implementation-spec §10・§12 M9 / DECISIONS D-035・D-036）。
 * 生データCSV出力 → 外部（Sheets/GAS）で採点 → 結果CSVを取込 → 確定（新しい版）→ 公開。
 * 全件を一つの版として扱い部分公開はしない。直前の公開版へロールバックできる。
 */
export function ScoreManagementPage(): ReactElement {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState(currentPeriod());

  const versionsQuery = useQuery({
    queryKey: ['score-versions', period],
    queryFn: () => fetchScoreVersions(period),
    enabled: Boolean(period),
  });

  const [job, setJob] = useState<CsvJob | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  let state: ListState = 'ready';
  if (versionsQuery.isPending) state = 'loading';
  else if (versionsQuery.isError) {
    state =
      versionsQuery.error instanceof ApiError && versionsQuery.error.status === 403
        ? 'forbidden'
        : 'error';
  }

  async function invalidate(targetPeriod: string): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['score-versions', targetPeriod] });
  }

  async function onFileSelected(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    setJob(null);
    try {
      const uploaded = await uploadScoreResultCsv(file);
      setJob(uploaded);

      // 取込先の月はCSVが持っている。画面の対象月をそれに合わせて、
      // 下の「版の一覧」が取り込んだ月を指すようにする（D-097）。
      // 取込のために事前に月を選ばせるのは冗長なため、選択ではなく追従にした
      const csvPeriod = uploaded.rows.find((row) => row.data?.period)?.data?.period;
      if (typeof csvPeriod === 'string' && csvPeriod && csvPeriod !== period) {
        setPeriod(csvPeriod);
        setNotice(`このCSVは ${csvPeriod} 分でした。対象月を ${csvPeriod} に切り替えました。`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'アップロードに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onConfirmCsv(): Promise<void> {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      const confirmed = await confirmScoreResultCsv(job.id, reason);
      setJob(confirmed);
      const confirmedPeriod = confirmed.rows[0]?.data.period;
      if (typeof confirmedPeriod === 'string' && confirmedPeriod) {
        setPeriod(confirmedPeriod);
        await invalidate(confirmedPeriod);
      } else {
        await invalidate(period);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '確定に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onPublish(versionId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await publishScoreVersion(versionId);
      await invalidate(period);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '公開に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onRollback(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await rollbackScoreVersion(period);
      await invalidate(period);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'ロールバックに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const hasCsvErrors = (job?.summary?.error_count ?? 0) > 0;
  const isCsvConfirmed = job?.status === 'confirmed';
  const hasPublished = (versionsQuery.data ?? []).some((v) => v.status === 'published');

  const columns: Column<ScoreVersion>[] = [
    { key: 'calculation_version', header: '計算版', primary: true, render: (v) => v.calculation_version },
    { key: 'status', header: '状態', render: (v) => SCORE_VERSION_STATUS_LABELS[v.status] },
    {
      key: 'published_at',
      header: '公開日時',
      render: (v) => (v.published_at ? formatDateTime(v.published_at) : '—'),
    },
    { key: 'created_at', header: '取込日時', render: (v) => formatDateTime(v.created_at) },
  ];

  return (
    <>
      <PageHeader title="採点管理" description="生データ出力・結果取込・公開・ロールバックを行います。" />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {notice && (
        <Notice kind="info" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}

      <div className="mb-4">
        <label className="block max-w-xs">
          <span className="mb-1.5 block text-wptext-muted">対象月</span>
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          />
          <span className="mt-1 block text-xs text-wptext-muted">
            「1. 生データ出力」と「3. 版の一覧」がこの月のものになります。
            「2. 結果CSVを取込」は<strong>CSVに書かれた月</strong>に取り込むので、
            ここで選び直す必要はありません（取り込むと対象月が自動で切り替わります）。
          </span>
        </label>
      </div>

      <div className="flex flex-col gap-4">
        <MetaBox title="1. 生データCSVを出力">
          <p className="mb-2 text-wptext-muted">
            <a
              href={scoreRawExportUrl(period)}
              className="text-wpaccent hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              対象月の生データをCSV出力
            </a>
            します。このファイルを表計算（Excel・スプレッドシート）で採点し、
            点数と順位を付けたものを下の「2」から取り込みます。
          </p>
          {/* 「生データはどこで登録するのか」が分からない、という指摘（D-095）。
              実際はどこかで入力するものではなく、日々の記録から自動集計している */}
          <details>
            <summary className="cursor-pointer text-wpaccent">
              生データには何が入っている？（どこから集めているか）
            </summary>
            <p className="mt-2 text-wptext-muted">
              生データは<strong>どこかで登録するものではなく</strong>、この月の記録から
              システムが自動で集計します。事務局が入力する必要はありません。
            </p>
            <ul className="mt-1 list-inside list-disc text-wptext-muted">
              <li>
                <strong>出席回数</strong>（<code>attendance_count</code>）… この月に開催された
                イベントの本編で「現地参加」「オンライン参加」だった回数。
                イベント詳細の「出欠」タブの結果から数えます
              </li>
              <li>
                <strong>提出数</strong>（<code>submission_count</code>）… この月の課題・アンケートのうち
                <strong>期限内に提出された</strong>件数。期限を過ぎた提出は数に入りません
              </li>
              <li>
                <strong>優秀者に選ばれた回数</strong>（<code>evaluation_raw</code>）…
                分科会などの後に理事・講師が入力した「優秀者選定」の回数
              </li>
              <li>
                <strong>態度フィードバック</strong>（<code>activity_raw</code>）… 同じく理事・講師が
                入力した good／bad の件数（<code>good:2;bad:0</code> の形式）
              </li>
            </ul>
            <p className="mt-2 text-wptext-muted">
              つまり生データを増やしたい場合は、出欠・アンケート提出・理事のフィードバック入力が
              揃っているかを確認してください。<strong>点数の付け方（重み付け）はこのシステムでは
              決めていません</strong>。表計算側の計算式が正本です。
            </p>
          </details>
        </MetaBox>

        <MetaBox title="2. 結果CSVを取込">
          <p className="mb-2 text-wptext-muted">
            採点済みのファイルを選ぶと、取り込む前に内容を確認できます（この時点では確定しません）。
            <strong>どの月に取り込むかはCSVの集計月の列で決まります</strong>
            （上の対象月を選び直す必要はありません）。
            はじめての場合は
            <a href={csvSampleUrl('score')} className="text-wpaccent hover:underline">
              サンプルCSVをダウンロード
            </a>
            して形を確認できます。
          </p>
          <details className="mb-2">
            <summary className="cursor-pointer text-wpaccent">
              取り込めるCSVの形式を見る
            </summary>
            <ul className="mt-1 list-inside list-disc text-xs text-wptext-muted">
              <li>会員No. … 生データCSVの値をそのまま使います</li>
              <li>
                集計月 … <code>2026-08</code> の形式。
                <strong>ファイル内で1か月分のみ</strong>（複数月の混在はエラー）
              </li>
              <li>総合点 … 数値。順位 … 1以上の整数（同点は同じ順位で構いません）</li>
              <li>
                計算版 … 例 <code>v1</code>。
                <strong>同じ月で同じ名前は再取込できません</strong>（採点をやり直すときは
                <code>v2</code> のように変えます）
              </li>
              <li>
                内訳_◯◯（任意）… 「内訳_」の後ろがそのまま画面の項目名になります（例 内訳_出席 →「出席」）
              </li>
            </ul>
          </details>
          <input
            type="file"
            accept=".csv,text/csv"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFileSelected(file);
            }}
          />

          {job && (
            <div className="mt-3">
              <ListTable<CsvRowResult>
                caption="採点結果CSVプレビュー"
                rows={job.rows}
                columns={[
                  { key: 'row_no', header: '行', render: (r) => r.row_no },
                  {
                    key: 'member_id',
                    header: '会員ID',
                    primary: true,
                    render: (r) => r.member_id ?? '—',
                  },
                  // 生値（assign / error）を出さない（D-086 と同じ方針 / D-095）
                  {
                    key: 'action',
                    header: '判定',
                    render: (r) => (r.action === 'error' ? 'エラー（取り込めません）' : '取り込む'),
                  },
                  { key: 'warnings', header: '警告', render: (r) => r.warnings.join(' / ') || '—' },
                  {
                    key: 'errors',
                    header: 'エラー',
                    render: (r) => (
                      <span className="text-wpnotice-error">{r.errors.join(' / ') || '—'}</span>
                    ),
                  },
                ]}
                rowKey={(row) => String(row.row_no)}
              />
              {!isCsvConfirmed &&
                (hasCsvErrors ? (
                  <p className="mt-3 text-wpnotice-error">
                    エラーが残っています。ファイルを修正して再アップロードしてください。
                  </p>
                ) : (
                  <div className="mt-3">
                    <label htmlFor="score-csv-reason" className="mb-1.5 block text-wptext-muted">
                      確定理由（監査ログに記録されます）
                    </label>
                    <textarea
                      id="score-csv-reason"
                      required
                      rows={2}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="mb-2 w-full max-w-xl rounded-sm border border-wpborder px-3 py-2"
                    />
                    <button
                      type="button"
                      disabled={busy || !reason.trim()}
                      onClick={() => void onConfirmCsv()}
                      className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
                    >
                      確定する（全件を一つの版として作成・部分公開はしません）
                    </button>
                  </div>
                ))}
            </div>
          )}
        </MetaBox>

        <MetaBox title="3. 版の一覧・公開・ロールバック">
          {hasPublished && (
            <div className="mb-3">
              <button
                type="button"
                disabled={busy}
                onClick={() => void onRollback()}
                className="rounded-sm border border-wpnotice-error px-3 py-1 text-wpnotice-error hover:bg-wpnotice-error/10 disabled:opacity-50"
              >
                現在の公開版をロールバック（直前の公開版に戻す）
              </button>
            </div>
          )}
          <ListTable<ScoreVersion>
            caption="採点バージョン一覧"
            rows={versionsQuery.data ?? []}
            columns={columns}
            rowKey={(v) => v.id}
            state={state}
            onRetry={() => void versionsQuery.refetch()}
            emptyTitle="この月の採点バージョンはまだありません"
            rowActions={[
              {
                label: '公開する（全件を一つの版として公開）',
                onSelect: (v) => void onPublish(v.id),
              },
            ]}
          />
        </MetaBox>
      </div>
    </>
  );
}
